import React, { useEffect, useRef } from 'react';
import { Box, Typography, Accordion, Form as LegoForm, Loader } from '@lucid/lego';
import type { FormField, UploadUrlResponse } from '@lucid/lego';
import type { Theme } from '../types';
import styles from './Form.module.scss';

type FormProps = {
  /** Theme for the component */
  theme: Theme;
  /** Form fields to render */
  formFields: FormField[];
  /** Title for the accordion */
  title?: string;
  /** Form ID */
  formId?: string;
  /** Success message to display */
  successMessage?: string;
  /** Error message to display */
  errorMessage?: string | null;
  /** Whether form is submitting */
  isSubmitting?: boolean;
  /** Callback when form submits successfully */
  onSuccess: (data: unknown) => void;
  /** Callback when form has validation errors */
  onError: (error: unknown) => void;
  /** Callback when form dirty state changes */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Ref for scrolling to top */
  containerRef?: React.RefObject<HTMLDivElement | null>;
  /** Function to get presigned upload URL for file uploads */
  getUploadUrl?: (file: File) => Promise<UploadUrlResponse>;
  /** Callback when file upload completes */
  onUploadComplete?: (fileUrl: string, file: File) => void;
  /** Callback when file is removed */
  onFileRemove?: (fileId?: string) => void;
  /** Show API response section for development reference @default false */
  showDevResponse?: boolean;
};

/**
 * Generic form accordion section for VinSearch slider
 * @component
 * @param {FormProps} props - Component props
 * @returns {JSX.Element | null} Accordion with form fields, or null if no fields
 * @example
 * <Form
 *   theme="dark"
 *   formFields={fields}
 *   title="Case Details"
 *   onSuccess={handleSuccess}
 *   onError={handleError}
 * />
 */
const Form: React.FC<FormProps> = ({
  theme,
  formFields,
  title = 'Form Details',
  formId = 'vin-search-form',
  successMessage = '',
  errorMessage = null,
  isSubmitting = false,
  onSuccess,
  onError,
  onDirtyChange,
  containerRef,
  getUploadUrl,
  onUploadComplete,
  onFileRemove,
  showDevResponse = false,
}) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const ref = containerRef || internalRef;

  // Scroll to top when success or error message appears
  useEffect(() => {
    if ((successMessage || errorMessage) && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [successMessage, errorMessage, ref]);

  if (!formFields || formFields.length === 0) return null;

  return (
    <Box ref={ref}>
      <Accordion title={title} theme={theme} defaultOpen={true} showBorder={true}>
        <Box className={styles.formContainer}>
          {successMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.successMessageText}>
              <Typography variant="body" size="medium" color="white" theme={theme}>
                {successMessage}
              </Typography>
            </Box>
          )}
          {errorMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
              <Typography variant="body" size="medium" color="white" theme={theme}>
                {errorMessage}
              </Typography>
            </Box>
          )}
          {isSubmitting && (
            <Box className={styles.fetchingOverlay}>
              <Loader size={32} />
            </Box>
          )}
          <LegoForm
            id={formId}
            theme={theme}
            formFields={formFields}
            onSuccess={onSuccess}
            onError={onError}
            onDirtyChange={onDirtyChange}
            isFluid={true}
            getUploadUrl={getUploadUrl}
            onUploadComplete={onUploadComplete}
            onFileRemove={onFileRemove}
            showDevResponse={showDevResponse}
          />
        </Box>
      </Accordion>
    </Box>
  );
};

export default Form;
